/**
 * The permission strings this app gates on.
 *
 * They are matched against `permissions` on the admin JWT, which is copied verbatim
 * from the `adminUsers` document at login (hitch-services
 * `services/authentication/routes/adminLoginRoutes.js`). There is no enum on the
 * server — the array is free-form JSON — so this file is the closest thing to a
 * registry, and a typo silently DENIES access rather than erroring. Hence constants
 * rather than inline strings.
 *
 * `useHasPermission` treats `ADMIN` as satisfying any of these, so a superuser needs no
 * entry added. Granting one to a non-admin is what needs the array updated — until
 * someone does, a newly-named permission behaves exactly like requiring ADMIN, which
 * makes introducing one a safe no-op rather than a lockout.
 *
 * Not yet exhaustively adopted: call sites outside the driver-profile work still pass
 * literals. Prefer these when touching one.
 */
export const PERMISSIONS = {
  /** Superuser. Implies every permission below — see useHasPermission. */
  ADMIN: 'admin',

  // ── Drivers ──────────────────────────────────────────────────────────────
  /** The application funnel: /drivers-approval and its triage grid. */
  CHANGE_DRIVER_STATUS: 'change_driver_status',
  VIEW_DRIVER: 'view_driver',
  REMOVE_BLOCK_DATE: 'remove_block_date',
  /** Templated driver pushes by city. */
  SEND_DRIVER_NOTIFICATIONS: 'send_driver_notifications',

  /**
   * The driver lists and a driver's profile.
   *
   * Separate from CHANGE_DRIVER_STATUS because it is a strictly larger power: the
   * profile decides documents and sets standing, which governs whether an
   * already-approved driver may keep driving, where the funnel only moves applicants.
   */
  MANAGE_DRIVERS: 'manage_drivers',

  /**
   * The make/model/year catalog.
   *
   * Its own permission rather than sharing MANAGE_DRIVERS: this is one shared table
   * every `driver_vehicle` points at, so a bad row is wrong for every driver holding
   * that vehicle — a different blast radius from editing one driver.
   */
  MANAGE_VEHICLE_CATALOG: 'manage_vehicle_catalog',

  // ── Users, money, rides ──────────────────────────────────────────────────
  EDIT_USER: 'edit_user',
  DELETE_USERS: 'delete_users',
  RAW_DATA: 'raw_data',
  CREDITS: 'credits',
  REFUND: 'refund',
  MANUAL_BONUS: 'manual_bonus',
  MAKE_IT_RIGHT: 'make_it_right',
  TRANSFER_RIDE: 'transfer_ride',
  EDIT_RIDE_LOCATION: 'edit_ride_location',
  REORDER_WAYPOINTS: 'reorder_waypoints',

  // ── Operations ───────────────────────────────────────────────────────────
  STATION_MGMT: 'station_mgmt',
  /** Trip throttling. Also behind the `admin-trip-throttling` GrowthBook flag. */
  TRIP_THROTTLE: 'trip_throttle',
  /**
   * Move a trip's departure time, cascading to every ride on the trip.
   *
   * Enforced in this app only — the admin API's AdminGuard authenticates that
   * the caller is an admin but reads no permissions array.
   */
  ADJUST_TRIP_TIME: 'adjust_trip_time',
  /**
   * Edit a lane's polyline geometry.
   *
   * Its own permission rather than sharing STATION_MGMT: a polyline is read
   * live at booking time — ST_DWithin against it decides which lane a trip
   * matches at all, and ST_Distance against it decides which stations are
   * usable — so a bad edit changes rider-facing bookability the moment it
   * commits, across every station on the lane at once.
   *
   * Gates this app only. As with ADJUST_TRIP_TIME, the admin API's AdminGuard
   * authenticates that the caller is an admin but reads no permissions array,
   * so this hides the UI rather than closing the endpoint.
   */
  EDIT_LANE_POLYLINE: 'edit_lane_polyline',
};
